/**
 * Public API docs page — /api-docs on cyannect.com (main host only).
 * Written in plain HTML/tailwind. Kept intentionally self-contained (no fancy
 * markdown pipeline) so it's cheap to update inline.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Code, Zap, Webhook, Key, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";
import SdkPlayground from "@/components/SdkPlayground";

// Simplified curl helper — pulled the host from the browser so partners
// can copy-paste against the same environment they're viewing.
function useApiHost() {
  const [host, setHost] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") setHost(window.location.origin);
  }, []);
  return host;
}

function CodeBlock({ children, testid }) {
  const copy = () => {
    navigator.clipboard.writeText(children);
    toast.success("Copied to clipboard");
  };
  return (
    <div className="relative group">
      <pre className="rounded-2xl bg-slate-900 text-slate-100 text-xs leading-relaxed p-4 overflow-x-auto" data-testid={testid}>
        <code>{children}</code>
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 px-2.5 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"
        data-testid={`${testid}-copy`}
      >
        <Copy className="w-3 h-3" /> Copy
      </button>
    </div>
  );
}

function Endpoint({ method, path, children, testid }) {
  const methodColor = {
    GET:  "bg-emerald-100 text-emerald-800 border-emerald-200",
    POST: "bg-cyan-100 text-cyan-800 border-cyan-200",
    DELETE: "bg-rose-100 text-rose-800 border-rose-200",
    PATCH: "bg-violet-100 text-violet-800 border-violet-200",
  }[method] || "bg-slate-100 text-slate-800 border-slate-200";
  return (
    <section className="pf-card p-6" data-testid={testid}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-bold tracking-wider ${methodColor}`}>{method}</span>
        <code className="font-mono text-sm text-slate-800">{path}</code>
      </div>
      <div className="mt-4 text-sm text-slate-700 space-y-4 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function SchemaTable({ rows }) {
  return (
    <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
      <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
        <tr>
          <th className="px-3 py-2 font-semibold">Field</th>
          <th className="px-3 py-2 font-semibold">Type</th>
          <th className="px-3 py-2 font-semibold">Description</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.field} className="border-t border-slate-100">
            <td className="px-3 py-2 font-mono text-slate-800">{r.field}</td>
            <td className="px-3 py-2 font-mono text-slate-500">{r.type}</td>
            <td className="px-3 py-2 text-slate-700">{r.desc}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ApiDocs() {
  const host = useApiHost();
  const base = host ? `${host}/api` : "https://cyannect.com/api";

  return (
    <div className="min-h-[calc(100vh-4rem)] relative">
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 opacity-70">
        <div className="blob blob-cyan blob-float w-[500px] h-[500px] top-10 -left-40" />
        <div className="blob blob-lav blob-float w-[500px] h-[500px] bottom-10 -right-40" style={{ animationDelay: "4s" }} />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16 space-y-10" data-testid="api-docs-page">
        <header>
          <div className="pf-chip pf-chip-cyan w-fit"><Code className="w-3 h-3" /> Partner API · v1</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mt-4">
            Build on <span className="pf-gradient-text-2">Cyannect</span>
          </h1>
          <p className="text-slate-600 mt-3 max-w-2xl leading-relaxed">
            This is the public reference for consuming Cyannect's live plans catalog and receiving conversion webhooks. Every endpoint prefixed with <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">/api</code> is served from the same host that ships this page.
          </p>
          <p className="text-xs text-slate-500 mt-3">Base URL: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded" data-testid="api-base-url">{base}</code></p>
        </header>

        {/* Auth summary */}
        <section className="pf-card p-6 flex items-start gap-4" data-testid="api-auth-note">
          <Key className="w-6 h-6 text-cyan-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-lg text-slate-900">Authentication</div>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Public catalog endpoints (like <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">GET /billing/plans</code>) require <strong>no auth</strong>. Partner endpoints require a merchant API key — mint one from your <a href="/merchant/api-keys" className="text-cyan-700 font-medium underline underline-offset-2" data-testid="link-api-keys-page">API keys page</a>. Send it as either:
            </p>
            <ul className="text-sm text-slate-600 list-disc pl-5 mt-2 space-y-1">
              <li><code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Authorization: Bearer cyk_live_...</code> <span className="text-slate-500">(recommended, standard)</span></li>
              <li><code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">X-Api-Key: cyk_live_...</code> <span className="text-slate-500">(also accepted)</span></li>
            </ul>
            <p className="text-xs text-slate-500 mt-3">All mutating requests from a browser must additionally include the CSRF header — auto-echoed by our JS SDK. Server-to-server calls with a Bearer/X-Api-Key are CSRF-exempt.</p>
          </div>
        </section>

        {/* Rate limits */}
        <section className="pf-card p-6" data-testid="api-rate-limits">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-emerald-600 shrink-0" />
            <div className="font-display text-lg text-slate-900">Rate limits</div>
          </div>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed">
            Every response on <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">/api/v1/*</code> includes the standard trio so you can back off proactively.
          </p>
          <div className="mt-4">
            <SchemaTable rows={[
              { field: "X-RateLimit-Limit",     type: "integer", desc: "Requests allowed per window on your current tier (currently 600 for keyed, 120 for anonymous)." },
              { field: "X-RateLimit-Remaining", type: "integer", desc: "Requests still available in the active window. Reaches 0 immediately before a 429." },
              { field: "X-RateLimit-Reset",     type: "integer", desc: "Unix epoch (seconds) when the window resets and remaining goes back to Limit." },
              { field: "Retry-After",           type: "integer", desc: "Only on a 429. Seconds until the window resets — respect it before retrying." },
            ]} />
          </div>
          <p className="text-xs text-slate-500 mt-3">Windows are fixed 60-second buckets keyed by API key when present, falling back to client IP for anonymous traffic. Over-budget calls receive a 429 with the four headers above and a JSON body of <code className="font-mono">&#123;detail, limit, window_seconds, retry_after_seconds&#125;</code>.</p>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Inspect the headers</div>
            <CodeBlock testid="rate-limit-example">{`curl -i ${base}/v1/ping | grep -i x-ratelimit
# X-RateLimit-Limit: 120
# X-RateLimit-Remaining: 119
# X-RateLimit-Reset: 1740751320`}</CodeBlock>
          </div>
        </section>

        {/* v1 Conversion & Offer Validation — Phase B (iter 38) */}
        <SdkPlayground />
        <Endpoint method="POST" path="/api/v1/offers/validate" testid="endpoint-offer-validate">
          <p>Called from the merchant's checkout right before applying the discount. Answers: is this <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">cy_ref</code> still valid, does it belong to this merchant, and which of the cart items are eligible?</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Request</div>
            <CodeBlock testid="offer-validate-req">{`{
  "cy_ref": "CY-8F72K91A",
  "currency": "CAD",
  "items": [
    { "sku": "SKU-AIRMAX", "quantity": 1, "unit_price": 180.00 }
  ]
}`}</CodeBlock>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Response</div>
            <CodeBlock testid="offer-validate-res">{`{
  "eligible": true,
  "offer_id": "cmp_...",
  "discount_type": "PERCENT",
  "discount_value": 15.0,
  "eligible_items": [ { "sku": "SKU-AIRMAX", "quantity": 1 } ],
  "eligible_subtotal_minor": 18000,
  "maximum_discount_minor": 2700,
  "maximum_discount_display": "$27.00",
  "currency": "CAD"
}`}</CodeBlock>
            <p className="text-xs text-slate-500 mt-2">Money is returned in <strong>integer minor units</strong> (cents) — the display string is a convenience. Merchant checkout remains authoritative for the final applied discount (taxes, inventory, other stacked promos), but the <code className="font-mono">maximum_discount_minor</code> is the upper bound Cyannect will attribute.</p>
          </div>
        </Endpoint>

        <Endpoint method="POST" path="/api/v1/conversions" testid="endpoint-conversion-create">
          <p>Report a confirmed order. <strong>Idempotent via <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Idempotency-Key</code></strong> — replays return the original conversion, they never create duplicates. Every money field is normalized to integer minor units on ingress.</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Request headers</div>
            <SchemaTable rows={[
              { field: "Authorization", type: "string", desc: "Bearer <api_key> — same as everywhere on /v1." },
              { field: "Idempotency-Key", type: "string", desc: "Required. Unique per unique order — replays are silently deduped." },
            ]} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Body</div>
            <CodeBlock testid="conversion-req">{`{
  "cy_ref": "CY-8F72K91A",
  "order_id": "ORD-88392",
  "currency": "CAD",
  "items": [
    { "product_id": "782", "variant_id": "9981",
      "sku": "SKU-AIRMAX", "name": "Nike Air Max 270",
      "quantity": 1, "unit_price": 180.00 }
  ],
  "subtotal": 180.00, "discount": 27.00,
  "tax": 19.89, "shipping": 0,
  "total": 172.89,
  "status": "paid",
  "purchased_at": "2026-02-28T14:22:07Z"
}`}</CodeBlock>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">State machine</div>
            <p className="text-xs text-slate-600 leading-relaxed">
              <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">PENDING</code> → <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">CONFIRMED</code> → <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">PAID</code> → <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">PARTIALLY_REFUNDED</code> / <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">REFUNDED</code> / <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">DISPUTED</code>.
            </p>
            <p className="text-xs text-slate-500 mt-1">Once a conversion is in a terminal state (REFUNDED / CANCELLED), no further transitions are permitted — <code className="font-mono">409 Conflict</code>.</p>
          </div>
        </Endpoint>

        <Endpoint method="PATCH" path="/api/v1/conversions/{id}" testid="endpoint-conversion-patch">
          <p>Report a refund, cancellation, or dispute. Enforces the state machine — an invalid transition returns <code className="font-mono">409</code>.</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Body</div>
            <CodeBlock testid="conversion-patch-req">{`{
  "status": "PARTIALLY_REFUNDED",
  "refunded_amount": 50.00,
  "reason": "customer_request"
}`}</CodeBlock>
            <p className="text-xs text-slate-500 mt-2">For <code className="font-mono">PARTIALLY_REFUNDED</code>, the amount must be strictly greater than 0 and strictly less than the original total.</p>
          </div>
        </Endpoint>

        {/* Attribution — POST /api/referrals/activate + GET /r/{cy_ref} */}
        <Endpoint method="POST" path="/api/referrals/activate" testid="endpoint-referral-activate">
          <p>Mints a Cyannect referral (<code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">CY-XXXXXXXX</code>) that a shopper's browser can use to visit a merchant with full attribution. Called from the Cyannect consumer app when a shopper taps <strong>Get offer</strong>. Idempotent within the 30-day attribution window.</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Request</div>
            <SchemaTable rows={[
              { field: "campaign_id", type: "string", desc: "The offer/campaign to attribute this referral to." },
            ]} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Response</div>
            <CodeBlock testid="referral-example-response">{`{
  "ok": true,
  "cy_ref": "CY-8F72K91A",
  "click_url": "https://cyannect.com/r/CY-8F72K91A",
  "expires_at": "2026-09-27T13:00:00Z",
  "reused": false
}`}</CodeBlock>
          </div>
        </Endpoint>

        <Endpoint method="GET" path="/r/{cy_ref}" testid="endpoint-referral-redirect">
          <p>Public safe-redirect endpoint. Validates the token, records a click, and <strong>302</strong>s the browser to the merchant's landing URL with <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">?cy_ref=CY-XXX</code> appended so first-party attribution can pick up on the merchant side.</p>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Behavior</div>
            <SchemaTable rows={[
              { field: "Valid + active",  type: "302",  desc: "Redirects to the merchant's registered landing URL. One billable click recorded (per 5-min dedupe window)." },
              { field: "Bot user agent",  type: "302",  desc: "Still redirects (we don't want to break users) but the click is flagged non-billable." },
              { field: "Unknown token",   type: "404",  desc: "Token doesn't exist or is structurally invalid." },
              { field: "Expired token",   type: "410",  desc: "Attribution window has elapsed. Marks the referral as EXPIRED." },
              { field: "Offer archived",  type: "410",  desc: "The underlying campaign was deleted after the token was minted." },
            ]} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Example</div>
            <CodeBlock testid="referral-redirect-example">{`GET /r/CY-8F72K91A HTTP/1.1
Host: cyannect.com

HTTP/1.1 302 Found
Location: https://merchant.com/products/air-max-270?cy_ref=CY-8F72K91A`}</CodeBlock>
          </div>
          <p className="text-xs text-slate-500 mt-3">Open-redirect protection: <code className="font-mono text-[11px]">cy_ref</code> tokens can <strong>only</strong> forward to a host that matches or is a subdomain of the campaign's registered landing URL — so <code>example.com.evil.tld</code> will never resolve, even if a merchant edits their landing URL after the token was minted.</p>
        </Endpoint>

        {/* GET /api/billing/plans */}
        <Endpoint method="GET" path="/api/billing/plans" testid="endpoint-plans">
          <p>Returns the live subscription catalog. Prices, cadences, and commission rates are the same values powering the public Pricing page and the merchant Subscription page — so partner integrations never drift out of sync.</p>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Response shape</div>
            <SchemaTable rows={[
              { field: "free.monthly",           type: "number",  desc: "$/month for the Free plan (always 0)." },
              { field: "plans.standard.tiers[]", type: "array",   desc: "Monthly, Quarterly, Annual cadences with id/monthly/upfront/currency/days/cadence/canonical_tier." },
              { field: "plans.pro.tiers[]",      type: "array",   desc: "Same shape as Standard." },
              { field: "performance_fee[]",      type: "array",   desc: "Per-plan commission: {plan, rate_pct, cap_cad, label}." },
              { field: "legacy_plans",           type: "object",  desc: "Retained legacy SKU catalog for backwards-compat consumers." },
            ]} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Example request</div>
            <CodeBlock testid="plans-example-curl">{`curl ${base}/billing/plans`}</CodeBlock>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Example response (trimmed)</div>
            <CodeBlock testid="plans-example-response">{`{
  "free": { "monthly": 0.0, "canonical_tier": "free", ... },
  "plans": {
    "standard": {
      "name": "Standard",
      "tiers": [
        { "id": "standard_monthly",   "monthly": 69.0, "upfront": 69.0,  "cadence": "monthly",   "canonical_tier": "standard" },
        { "id": "standard_quarterly", "monthly": 64.0, "upfront": 192.0, "cadence": "quarterly", "canonical_tier": "standard" },
        { "id": "standard_annual",    "monthly": 59.0, "upfront": 708.0, "cadence": "annual",    "canonical_tier": "standard" }
      ]
    },
    "pro": { "tiers": [ { "id": "pro_monthly", "monthly": 99.0, ... } ] }
  },
  "performance_fee": [
    { "plan": "free",     "rate_pct": 5.99, "cap_cad": 49.99 },
    { "plan": "standard", "rate_pct": 3.99, "cap_cad": 30.00 },
    { "plan": "pro",      "rate_pct": 3.49, "cap_cad": 30.00 }
  ]
}`}</CodeBlock>
          </div>
        </Endpoint>

        {/* POST /api/webhook/conversion */}
        <Endpoint method="POST" path="/api/webhook/conversion" testid="endpoint-conversion-webhook">
          <p>Fires whenever a verified conversion is recorded against one of your campaigns. Delivery is at-least-once — dedupe on <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">event_id</code>. Retries follow exponential backoff up to 24h before a delivery is marked failed.</p>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Payload</div>
            <SchemaTable rows={[
              { field: "event_id",       type: "string", desc: "Idempotency key. Store and dedupe." },
              { field: "event_type",     type: "string", desc: "\"conversion.verified\" or \"conversion.reversed\"." },
              { field: "campaign_id",    type: "string", desc: "The Cyannect campaign that produced this conversion." },
              { field: "coupon_code",    type: "string", desc: "The exact code redeemed (uppercase). Null for auto_apply campaigns." },
              { field: "customer_hash",  type: "string", desc: "SHA-256 of the shopper's Cyannect user id (opaque)." },
              { field: "sale_amount",    type: "number", desc: "Verified pre-tax subtotal in CAD." },
              { field: "fee_rate",       type: "number", desc: "Commission rate applied at write time (fractional, e.g. 0.0399)." },
              { field: "fee_cad",        type: "number", desc: "Commission owed to Cyannect in CAD (already capped)." },
              { field: "plan_at_time",   type: "string", desc: "Merchant tier when the fee was calculated: free|standard|pro." },
              { field: "occurred_at",    type: "string", desc: "ISO-8601 UTC timestamp of the redemption." },
            ]} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Example payload</div>
            <CodeBlock testid="conversion-example-payload">{`{
  "event_id": "evt_01HZK9RM3B8XCT4RVE5YQ2P0S6",
  "event_type": "CONVERSION_RECEIVED",
  "conversion_id": "conv_7f2e4a01",
  "campaign_id": "cmp_7f2e4a01",
  "coupon_code": "SAVE15-A9F2K",
  "customer_hash": "b91d92c8...4a2f",
  "sale_amount": 128.50,
  "currency": "CAD",
  "fee_rate": 0.0399,
  "fee_cad": 5.13,
  "fee_capped": false,
  "plan_at_time": "standard",
  "occurred_at": "2026-02-28T14:22:07Z"
}`}</CodeBlock>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Signature verification</div>
            <p>Every webhook carries an <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">X-Cyannect-Signature</code> header — an HMAC-SHA256 of the raw body using your webhook secret. Reject any request where the signatures don't match.</p>
            <CodeBlock testid="conversion-example-verify">{`# python
import hmac, hashlib
expected = hmac.new(WEBHOOK_SECRET.encode(), body, hashlib.sha256).hexdigest()
if not hmac.compare_digest(expected, request.headers["X-Cyannect-Signature"]):
    return 401`}</CodeBlock>
          </div>
        </Endpoint>

        {/* Ping */}
        <section className="pf-card p-6 flex items-start justify-between gap-6 flex-wrap" data-testid="api-docs-cta">
          <div className="flex items-start gap-4">
            <Webhook className="w-6 h-6 text-cyan-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg text-slate-900">Need a sandbox key?</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed max-w-xl">
                Every merchant can spin up an API key + webhook secret from the merchant portal — no waiting on us. Sandbox mode fires webhooks against your local URL with fully synthetic conversions.
              </p>
            </div>
          </div>
          <a href="/register?role=merchant" className="pf-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="api-docs-signup-cta">
            Create a merchant account <ArrowRight className="w-4 h-4" />
          </a>
        </section>

        <div className="text-center text-xs text-slate-500" data-testid="api-docs-footer">
          Have questions? Email <a href="mailto:developers@cyannect.com" className="text-cyan-700 font-medium">developers@cyannect.com</a>.
        </div>
      </div>
    </div>
  );
}
